// src/UserContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { API_ENDPOINTS } from "./config";
import { API_SCOPE, SILENT_REDIRECT_URI } from "./authConfig";
import { recoverWithInteractiveLogin } from './utils/authInteractiveFallback';

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const [userProfile, setUserProfile] = useState(() => {
    const cached = sessionStorage.getItem("userProfile");
    console.log("UserContext: Initializing userProfile from sessionStorage", cached ? "FOUND" : "NOT FOUND");
    return cached ? JSON.parse(cached) : null;
  });
  const [orgProfile, setOrgProfile] = useState(() => {
    const cached = sessionStorage.getItem("orgProfile");
    console.log("UserContext: Initializing orgProfile from sessionStorage", cached ? "FOUND" : "NOT FOUND");
    return cached ? JSON.parse(cached) : null;
  });
  const [subscriptionState, setSubscriptionState] = useState(() => {
    const cached = sessionStorage.getItem("subscriptionState");
    console.log("UserContext: Initializing subscriptionState from sessionStorage", cached ? "FOUND" : "NOT FOUND");
    return cached ? JSON.parse(cached) : null;
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [authBlocked, setAuthBlocked] = useState(false);

  // Function to manually refresh profile (e.g., after creating a grant)
  const refreshProfile = async () => {
    if (!accounts || accounts.length === 0) return;
    const accountForToken = instance.getActiveAccount() || accounts[0];
    // If multiple accounts are cached but none is set as active, abort.
    // Picking accounts[0] would be a guess and could use the wrong account.
    // AuthCallbackHandler will call setActiveAccount() and trigger a re-render.
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount && accounts.length > 1) {
      console.log('UserContext: Multiple accounts cached, no active account set - skipping refresh until active account is resolved');
      return;
    }
    try {
      setAuthBlocked(false);
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [API_SCOPE],
        account: accountForToken,
        redirectUri: SILENT_REDIRECT_URI
      });

      const res = await fetch(`${API_ENDPOINTS.auth}/GetUserProfile`, {
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
      });
      
      if (!res.ok) throw new Error(`GetUserProfile failed: ${res.status}`);
      const data = await res.json();

      console.log("UserContext: Profile refreshed", data);
      setUserProfile(data.user);
      setOrgProfile(data.org);
      setSubscriptionState(data.subscriptionState);

      sessionStorage.setItem("userProfile", JSON.stringify(data.user));
      sessionStorage.setItem("orgProfile", JSON.stringify(data.org));
      sessionStorage.setItem("subscriptionState", JSON.stringify(data.subscriptionState));
    } catch (err) {
      const recovered = await recoverWithInteractiveLogin({
        instance,
        error: err,
        account: accountForToken,
        reason: 'profile_refresh_token_failure'
      });
      if (recovered) {
        setAuthBlocked(true);
        return;
      }
      console.error("Error refreshing profile:", err);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!accounts || accounts.length === 0) return;
      
      // If we already have profile from sessionStorage, don't fetch unless it's empty
      const hasProfile = userProfile && orgProfile;
      if (hasProfile) {
        console.log("UserContext: Profile already loaded from sessionStorage, skipping API fetch");
        return;
      }
      
      setProfileLoading(true);
      const accountForToken = instance.getActiveAccount() || accounts[0];
      try {
        setAuthBlocked(false);
        // If multiple accounts are cached but none is set as active, abort.
        // Picking accounts[0] would be a guess and could use the wrong account.
        // AuthCallbackHandler will call setActiveAccount() and trigger a re-render.
        const activeAccount = instance.getActiveAccount();
        if (!activeAccount && accounts.length > 1) {
          console.warn('UserContext: Multiple accounts cached with no active account - forcing re-auth for explicit account selection');
          setAuthBlocked(true);
          setProfileLoading(false);
          return;
        }
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: [API_SCOPE],
          account: accountForToken,
          redirectUri: SILENT_REDIRECT_URI
        });

        const res = await fetch(`${API_ENDPOINTS.auth}/GetUserProfile`, {
          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        });
        
        // Handle 404 for new users - they'll go through onboarding
        if (res.status === 404) {
          console.log("UserContext: Profile 404 - new user will complete onboarding");
          setAuthBlocked(false);
          setProfileLoading(false);
          return;
        }
        
        if (!res.ok) throw new Error(`GetUserProfile failed: ${res.status}`);
        const data = await res.json();

        console.log("UserContext: Profile fetched successfully", data);
        setUserProfile(data.user);
        setOrgProfile(data.org);
        setSubscriptionState(data.subscriptionState);
        setAuthBlocked(false);

        sessionStorage.setItem("userProfile", JSON.stringify(data.user));
        sessionStorage.setItem("orgProfile", JSON.stringify(data.org));
        sessionStorage.setItem("subscriptionState", JSON.stringify(data.subscriptionState));
        console.log("UserContext: Saved to sessionStorage");
        setProfileLoading(false);
      } catch (err) {
        const recovered = await recoverWithInteractiveLogin({
          instance,
          error: err,
          account: accountForToken,
          reason: 'profile_fetch_token_failure'
        });
        if (recovered) {
          setAuthBlocked(true);
          setProfileLoading(false);
          return;
        }
        console.error("Error fetching profile:", err);
        setAuthBlocked(false);
        setProfileLoading(false);
      }
    };

    // ✅ Only fetch once MSAL is initialized and not in progress
    // Don't fetch on logout page
    // Only require userProfile - orgProfile is optional (consultants don't have one)
    const isLogoutPage = window.location.pathname === '/logout';
    const isOnboardingPage = window.location.pathname.startsWith('/onboarding');
    const publicRoutes = new Set([
      '/',
      '/about',
      '/faq',
      '/plans',
      '/tour',
      '/resources',
      '/contact',
      '/privacy',
      '/terms',
      '/ai-transparency',
      '/dpa',
      '/trust-center'
    ]);
    const isPublicRoute = publicRoutes.has(window.location.pathname);
    const needsOnboarding = sessionStorage.getItem('needsOnboarding') === 'true';
    const needsProfile = !userProfile; // Only check for user profile, not org
    const hasAccount = accounts && accounts.length > 0;
    
    console.log("UserContext useEffect check:", {
      inProgress,
      needsProfile,
      isLogoutPage,
      isOnboardingPage,
      isPublicRoute,
      needsOnboarding,
      hasAccount,
      accountsLength: accounts?.length || 0,
      willFetch: inProgress === InteractionStatus.None && needsProfile && !isLogoutPage && !isOnboardingPage && !isPublicRoute && !needsOnboarding && hasAccount
    });
    
    // Skip fetching profile if:
    // - On logout page
    // - On onboarding page (user doesn't exist yet)
    // - needsOnboarding flag is set (new user flow)
    if (inProgress === InteractionStatus.None && needsProfile && !isLogoutPage && !isOnboardingPage && !isPublicRoute && !needsOnboarding && hasAccount) {
      console.log("UserContext: Triggering fetchProfile()");
      fetchProfile();
    } else if (!hasAccount) {
      setAuthBlocked(false);
    }
  }, [accounts, instance, inProgress, userProfile, orgProfile]);

  console.log("UserContext.Provider rendering with:", { 
    userProfile: userProfile ? "EXISTS" : "NULL", 
    orgProfile: orgProfile ? "EXISTS" : "NULL",
    subscriptionState: subscriptionState ? "EXISTS" : "NULL",
    authBlocked
  });

  // Directly set profiles (used by invitation flow to push new user data into React state)
  const setProfiles = (user, org, subscription) => {
    setUserProfile(user || null);
    setOrgProfile(org || null);
    setSubscriptionState(subscription || null);
    setAuthBlocked(false);
    if (user) sessionStorage.setItem('userProfile', JSON.stringify(user));
    if (org) sessionStorage.setItem('orgProfile', JSON.stringify(org));
    if (subscription) sessionStorage.setItem('subscriptionState', JSON.stringify(subscription));
  };

  return (
    <UserContext.Provider value={{ userProfile, orgProfile, subscriptionState, profileLoading, authBlocked, refreshProfile, setProfiles }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}